/**
 * Global crash handlers
 *
 * Logs uncaught errors (`window` 'error') and unhandled promise rejections
 * ('unhandledrejection') through the logger pipeline at error level, with a
 * stack. Installed once at bootstrap (main.tsx); user-facing behaviour is
 * unchanged (nothing is prevented or swallowed).
 *
 * A crash handler must never throw or recurse: every handler body is wrapped
 * in try/catch and guarded by a re-entrancy flag.
 */

import { logger } from '@/utils/logger';
import { toError } from '@/utils/to-error';
import { describeUrlForLog } from '@/utils/logged-fetch';

export enum CrashSource {
  WINDOW_ERROR = 'window_error',
  UNHANDLED_REJECTION = 'unhandled_rejection',
}

let removeHandlers: (() => void) | null = null;
let handling = false;

function guarded(fn: () => void): void {
  if (handling) return;
  handling = true;
  try {
    fn();
  } catch {
    // Never throw from a crash handler.
  } finally {
    handling = false;
  }
}

/** `reason_type` is this value for Error reasons, else `typeof reason`. */
enum RejectionReasonType {
  ERROR = 'error',
}

/** Benign browser notifications that arrive as error events with no error. */
// Both Chrome variants: "...completed with undelivered notifications" and "...limit exceeded".
const BENIGN_MESSAGE = /^ResizeObserver loop/;

function onWindowError(event: ErrorEvent): void {
  guarded(() => {
    if (!event.error && BENIGN_MESSAGE.test(event.message ?? '')) return;
    logger.error(
      'Uncaught error',
      toError(event.error, event.message || 'Uncaught error'),
      {
        source: CrashSource.WINDOW_ERROR,
        message: event.message,
        // Script URLs may carry query tokens: log host+path only.
        filename: event.filename
          ? describeUrlForLog(event.filename)
          : undefined,
        lineno: event.lineno,
        colno: event.colno,
      }
    );
  });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  guarded(() => {
    const reason: unknown = event.reason;
    logger.error(
      'Unhandled promise rejection',
      toError(reason, 'Unhandled promise rejection'),
      {
        source: CrashSource.UNHANDLED_REJECTION,
        reason_type:
          reason instanceof Error ? RejectionReasonType.ERROR : typeof reason,
      }
    );
  });
}

/**
 * Installs the global listeners. Idempotent: a second call adds nothing and
 * returns the same remover. The returned function removes the listeners
 * (used by tests).
 */
export function installCrashHandlers(): () => void {
  if (removeHandlers) return removeHandlers;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  removeHandlers = () => {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    removeHandlers = null;
  };
  return removeHandlers;
}
